import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface CarpetItem {
    dimensions: { length: number; width: number };
    unit: 'cm' | 'inch' | 'sqft';
    sqft: number;
    price: number;
}

interface CarpetCalculatorProps {
    basePrice: number;  // Price per sqft
    onCalculate: (service: { title: string; price: string }) => void;
    selectedServices: Set<{ title: string; price: string }>;  // Add this prop
}

const CarpetCalculator = ({ basePrice, onCalculate, selectedServices }: CarpetCalculatorProps) => {
    const { t } = useTranslation();
    const [dimensions, setDimensions] = useState({ length: '', width: '' });
    const [unit, setUnit] = useState<'cm'|'inch'|'sqft'>('sqft');
    const [showCalculator, setShowCalculator] = useState(false);
    const [carpetItems, setCarpetItems] = useState<CarpetItem[]>([]);
    const isEditing = useRef(false);
    
    // Initialize carpetItems from selectedServices if necessary
    useEffect(() => {
        // Check if we already have carpet items, if so don't reinitialize
        if (carpetItems.length > 0) return;
        
        // Look for carpet items in the selectedServices
        const existingCarpetService = Array.from(selectedServices).find(
            service => service.title.includes(t('calculators.carpet.carpet'))
        );

        if (existingCarpetService) {
            // Try to parse information about individual carpets from the title
            const carpets: CarpetItem[] = [];
            const carpetRegex = new RegExp(`${t('calculators.carpet.carpet')} \\d+: (\\d+\\.?\\d*)×(\\d+\\.?\\d*) (\\w+) \\(([\\d.]+) sqft\\)`, 'g');
            let match;
            
            const title = existingCarpetService.title;
            while ((match = carpetRegex.exec(title)) !== null) {
                const length = parseFloat(match[1]);
                const width = parseFloat(match[2]);
                const unit = match[3] as 'cm' | 'inch' | 'sqft';
                const sqft = parseFloat(match[4]);
                
                carpets.push({
                    dimensions: { length, width },
                    unit,
                    sqft,
                    price: sqft * basePrice
                });
            }
            
            if (carpets.length > 0) {
                setCarpetItems(carpets);
            }
        }
    }, [selectedServices, basePrice, carpetItems.length]);
    
    // Convert measurements to sqft
    const convertToSqft = (length: number, width: number, unit: string): number => {
        switch(unit) {
            case 'cm':
                return (length * width) / 929.0304; // 1 sqft = 929.0304 sq cm
            case 'inch':
                return (length * width) / 144; // 1 sqft = 144 sq inches
            default:
                return length * width;
        }
    };

    // Calculate sqft for current input
    const calculateCurrentSqft = (): number => {
        const length = parseFloat(dimensions.length);
        const width = parseFloat(dimensions.width);
        if (isNaN(length) || isNaN(width)) return 0;
        return convertToSqft(length, width, unit);
    };
    
    // Get total sqft and price for all carpet items
    const getTotalCarpetInfo = (): { totalSqft: number; totalPrice: number } => {
        if (carpetItems.length === 0) {
            // Check in selectedServices if there are any carpets
            let totalSqft = 0;
            let totalPrice = 0;

            Array.from(selectedServices).forEach(service => {
                if (service.title.includes(t('calculators.carpet.carpet'))) {
                    // Try to extract the total sqft
                    const match = service.title.match(/total ([\d.]+) sqft/);
                    if (match) {
                        const sqft = parseFloat(match[1]);
                        totalSqft = sqft;
                        totalPrice = parseFloat(service.price.replace('RM', ''));
                    }
                }
            });
            
            return { totalSqft, totalPrice };
        }
        
        // Calculate from carpetItems
        const totalSqft = carpetItems.reduce((sum, item) => sum + item.sqft, 0);
        const totalPrice = carpetItems.reduce((sum, item) => sum + item.price, 0);
        return { totalSqft, totalPrice };
    };

    const handleAddCarpet = () => {
        const length = parseFloat(dimensions.length);
        const width = parseFloat(dimensions.width);
        const sqft = calculateCurrentSqft();
        
        if (sqft > 0) {
            // Add to carpet items
            setCarpetItems(prev => [
                ...prev, 
                {
                    dimensions: { length, width },
                    unit,
                    sqft,
                    price: sqft * basePrice
                }
            ]);
            
            // Reset dimensions for next entry
            setDimensions({ length: '', width: '' });
            isEditing.current = false;
        }
    };
    
    const handleRemoveCarpet = (index: number, event: React.MouseEvent) => {
        // Prevent default behavior and stop event propagation
        event.preventDefault();
        event.stopPropagation();
        
        setCarpetItems(prev => prev.filter((_, i) => i !== index));
    };
    
    const handleSaveToBooking = () => {
        if (carpetItems.length > 0) {
            const totalInfo = getTotalCarpetInfo();
            
            // Format carpet items on a single line
            const itemDetails = carpetItems.map((item, index) =>
                `${t('calculators.carpet.carpetNumber', { number: index + 1 })}: ${item.dimensions.length}${item.unit === 'sqft' ? '×' : 'x'}${item.dimensions.width} ${item.unit} (${item.sqft.toFixed(2)} sqft)`
            ).join(', ');

            // Create the carpet service with the simpler format
            const carpetService = {
                title: `${t('calculators.carpet.carpet')} (${itemDetails})`,
                price: `RM${totalInfo.totalPrice.toFixed(2)}`
            };
            
            onCalculate(carpetService);
            setShowCalculator(false);
        }
    };

    const handleToggle = () => {
        setShowCalculator(!showCalculator);
        if (!showCalculator) {
            setDimensions({ length: '', width: '' });
            // Don't reset carpetItems here to preserve them
        }
    };

    const handleInputChange = (field: 'length' | 'width', value: string) => {
        isEditing.current = true;
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setDimensions(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };
    
    const formatUnitLabel = (unit: string): string => {
        switch(unit) {
            case 'cm': return 'cm';
            case 'inch': return t('calculators.carpet.unitInch');
            case 'sqft': return 'sqft';
            default: return unit;
        }
    };

    // Get total info for display
    const carpetInfo = getTotalCarpetInfo();
    const hasCarpets = carpetInfo.totalSqft > 0;
    const hasCurrentDimensions = calculateCurrentSqft() > 0;

    return (
        <div>
            <button
                type="button"
                onClick={handleToggle}
                className={`px-3 py-1.5 rounded-md text-xs flex items-center ${
                    hasCarpets
                        ? 'bg-blue-800 text-white'
                        : showCalculator
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700'
                }`}
            >
                {hasCarpets
                    ? `${t('calculators.carpet.carpet')} (${carpetInfo.totalSqft.toFixed(2)} sqft) - RM${carpetInfo.totalPrice.toFixed(2)}`
                    : `${t('calculators.carpet.carpet')} RM${basePrice.toFixed(2)}${t('calculators.carpet.perSqft')}`
                }
            </button>

            {showCalculator && (
                <div className="mt-2 space-y-3">
                    {/* Current carpet dimensions input */}
                    <div className="space-y-2 border p-3 rounded-md bg-gray-50">
                        <label className="block text-sm text-gray-700 font-medium">
                            {t('calculators.carpet.carpetSize')}
                        </label>
                        <div className="flex gap-2 items-center">
                            <input
                                type="text"
                                inputMode="decimal"
                                value={dimensions.length}
                                onChange={(e) => handleInputChange('length', e.target.value)}
                                onFocus={() => { isEditing.current = true; }}
                                className="w-full border rounded-md p-2 h-10 text-sm"
                                placeholder={t('calculators.carpet.length')}
                            />
                            <span className="text-sm">×</span>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={dimensions.width}
                                onChange={(e) => handleInputChange('width', e.target.value)}
                                onFocus={() => { isEditing.current = true; }}
                                className="w-full border rounded-md p-2 h-10 text-sm"
                                placeholder={t('calculators.carpet.width')}
                            />
                            <select
                                value={unit}
                                onChange={(e) => setUnit(e.target.value as 'cm'|'inch'|'sqft')}
                                className="border rounded-md p-2 h-10 text-sm"
                            >
                                <option value="cm">cm</option>
                                <option value="inch">inch</option>
                                <option value="sqft">sqft</option>
                            </select>
                        </div>
                        
                        {hasCurrentDimensions && (
                            <div className="text-sm mt-2">
                                <span>{calculateCurrentSqft().toFixed(2)} sqft - RM{(basePrice * calculateCurrentSqft()).toFixed(2)}</span>
                            </div>
                        )}
                        
                        <button
                            type="button"
                            onClick={handleAddCarpet}
                            disabled={!hasCurrentDimensions}
                            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-500"
                        >
                            {t('calculators.carpet.addCarpet')}
                        </button>
                    </div>

                    {/* List of added carpets */}
                    {carpetItems.length > 0 && (
                        <div className="border rounded-md p-3">
                            <h4 className="font-medium text-sm mb-2">{t('calculators.carpet.carpetList')}</h4>
                            <ul className="space-y-2 max-h-[200px] overflow-y-auto">
                                {carpetItems.map((carpet, index) => (
                                    <li key={index} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                                        <span>
                                            {t('calculators.carpet.carpetNumber', { number: index + 1 })}: {carpet.dimensions.length}×{carpet.dimensions.width} {formatUnitLabel(carpet.unit)} 
                                            <span className="text-gray-500 ml-1">
                                                ({carpet.sqft.toFixed(2)} sqft - RM{carpet.price.toFixed(2)})
                                            </span>
                                        </span>
                                        <button 
                                            onClick={(e) => handleRemoveCarpet(index, e)}
                                            className="text-red-500 hover:text-red-700 ml-2"
                                            aria-label="Remove carpet"
                                        >
                                            ✕
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            
                            {/* Total summary */}
                            <div className="mt-3 pt-3 border-t">
                                <div className="flex justify-between items-center font-medium">
                                    <span>{t('calculators.carpet.total')}</span>
                                    <span>
                                        {carpetInfo.totalSqft.toFixed(2)} sqft - RM{carpetInfo.totalPrice.toFixed(2)}
                                    </span>
                                </div>
                            </div>

                            {/* Save to booking button */}
                            <button
                                type="button"
                                onClick={handleSaveToBooking}
                                className="w-full mt-3 bg-blue-800 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700"
                            >
                                {t('calculators.carpet.addToBooking')}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CarpetCalculator; 